import React from 'react';
import camelCase from 'lodash/camelCase';
import mapKeys from 'lodash/mapKeys';
import { deepKeysToCamel } from '@eb/transformation-utils';
import { useInfiniteQuery } from 'react-query';

import {
    transformDestinationEvent,
    DEFAULT_DESTINATION_EXPANSIONS,
} from '@eb/event-renderer';
import { sdkRequest } from '@eb/http';

import { BrowseDataContext } from '../../context';
import { CARD_INFO_LOOKUP_MAP } from '../../../../utils/general';
import {
    LOCATION_DIALOG_CONTENT,
    ARTIST_SOCIAL_LINKS_PROPTYPE,
    LOCATION_SUGGESTION_PROPTYPE,
    ENTITY_TYPE_MAP,
    EVENT_TYPE,
} from '../../../../constants';

const transformBuckets = ({ buckets, expanded }) => {
    //snake to camel each key
    //if events format the events
    //put events/collections/whatever under "content"
    return buckets.map(({ type, ...bucket }) => {
        let content = [];
        const contentKey = ENTITY_TYPE_MAP[type];
        const shallowCamelBucket = mapKeys(bucket, (_val, key) =>
            camelCase(key),
        );

        if (contentKey) {
            if (type === EVENT_TYPE) {
                content = shallowCamelBucket[contentKey].map(
                    transformDestinationEvent,
                );
            } else {
                const camelBucket = deepKeysToCamel(bucket);
                content = CARD_INFO_LOOKUP_MAP[type]({
                    bucketsContent: camelBucket[contentKey],
                });
            }
        }

        return {
            ...shallowCamelBucket,
            content,
            type,
            isContentExpanded: expanded,
        };
    });
};

const fetchBrowseBuckets = ({ queryKey, pageParam }) => {
    const [_, placeId, isOnline] = queryKey;
    const { excludedBuckets } = pageParam;

    return sdkRequest('/api/v3/destination/city-browse/', {
        method: 'POST',
        body: JSON.stringify({
            place_id: placeId,
            online_events_only: isOnline,
            buckets_to_exclude: excludedBuckets,
            'expand.destination_event': DEFAULT_DESTINATION_EXPANSIONS,
        }),
    }).then((res) => {
        return {
            buckets: transformBuckets({ buckets: res.buckets, expanded: true }),
            excludedBuckets: res.excluded_buckets,
            moreBuckets: res.more_buckets,
        };
    });
};

export const MainContentDataLayer = (props) => {
    const { children, placeId, isOnline } = props;
    const { data, fetchNextPage, hasNextPage } = useInfiniteQuery(
        ['browse-buckets', placeId, isOnline],
        fetchBrowseBuckets,
        {
            getNextPageParam: (prevPage) => {
                if (prevPage.moreBuckets) {
                    return {
                        excludedBuckets: prevPage.excludedBuckets,
                    };
                }
            },
            initialData: {
                pages: [
                    {
                        buckets: transformBuckets({
                            buckets: props.buckets,
                            expanded: false,
                        }),
                        excludedBuckets: props.excludedBuckets,
                        moreBuckets: props.moreBuckets,
                    },
                ],
                pageParams: [{ excludedBuckets: props.excludedBuckets }],
            },
        },
    );

    return (
        <BrowseDataContext.Provider
            value={{
                fetchNextPage,
                data,
                hasNextPage,
                buckets: data.pages.reduce(
                    (memo, page) => [...memo, ...page.buckets],
                    [],
                ),
            }}
        >
            {children}
        </BrowseDataContext.Provider>
    );
};
